import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, CheckCircle2, MapPin, Star, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function LeadMatch() {
  const { leadId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [date, setDate] = useState("");
  const [assigned, setAssigned] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager", "staff"].includes(user.role)) { navigate("/dashboard"); return; }
    api.get(`/match/lead/${leadId}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load matches"));
  }, [user, loading, navigate, leadId]);

  const assign = async (caregiverId) => {
    try { await api.post("/match/assign", { lead_id: leadId, caregiver_id: caregiverId, date: date || undefined }); toast.success("Caregiver assigned & notified"); setAssigned({ ...assigned, [caregiverId]: true }); }
    catch { toast.error("Assign failed"); }
  };

  if (loading || !user || !data) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;
  const l = data.lead;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="lead-match-page">
      <button onClick={() => navigate("/ops/crm")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-900 mb-4"><ArrowLeft className="w-4 h-4" /> Back to CRM</button>
      <div className="flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-teal-500" /><p className="text-xs uppercase tracking-[0.16em] text-teal-600 font-semibold">T5 AI Match</p></div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">Top matches for {l.name}</h1>
      <p className="text-slate-600 mt-1">{l.care_need} · {l.location || "location not specified"} · {l.schedule || "schedule flexible"}</p>
      <div className="mt-4 flex items-center gap-2">
        <span className="text-sm text-slate-500">Proposed start date:</span>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="match-date" className="max-w-[180px]" />
        <span className="text-xs text-slate-400">Human staff confirms every assignment.</span>
      </div>

      <div className="mt-6 space-y-3">
        {data.matches.map((m, i) => (
          <motion.div key={m.caregiver.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex gap-4" data-testid={`match-${m.caregiver.id}`}>
            <div className="text-center shrink-0">
              <div className={`grid place-items-center w-16 h-16 rounded-full text-white font-display text-xl font-bold ${m.score >= 85 ? "bg-teal-600" : m.score >= 70 ? "bg-blue-800" : "bg-slate-400"}`}>{m.score}%</div>
              <p className="text-[10px] text-slate-400 mt-1">match</p>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-lg font-bold text-blue-950">{m.caregiver.name}</h3>
                <span className="flex items-center gap-1 text-xs text-amber-600"><Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> {m.caregiver.rating}</span>
                <span className="flex items-center gap-1 text-xs text-slate-500"><MapPin className="w-3.5 h-3.5" /> {m.caregiver.location}</span>
              </div>
              <ul className="mt-2 space-y-1">
                {m.reasons.map((r, j) => <li key={j} className="flex items-start gap-1.5 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" /> {r}</li>)}
              </ul>
              <div className="mt-3">
                {assigned[m.caregiver.id]
                  ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700"><CheckCircle2 className="w-4 h-4" /> Assigned</span>
                  : <Button onClick={() => assign(m.caregiver.id)} data-testid={`match-assign-${m.caregiver.id}`} size="sm" className="bg-teal-600 hover:bg-teal-700">Assign to {l.name.split(" ")[0]}</Button>}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
